/**
 * Server + client: level / EXP pool derived from lifetime `Player.totalExp`.
 * Must stay aligned with battle/learn UI (`deriveCharacterStatsFromTotalExp`).
 */
export const CHARACTER_STARTING_LEVEL = 1;
export const CHARACTER_STARTING_EXP = 0;
export const CHARACTER_STARTING_REQUIRED_EXP = 100;
export const REQUIRED_EXP_MULTIPLIER = 1.5;

/** Lifetime stat/ability budgets use these per level (learn HUD, notifications, apply). */
export const STAT_POINTS_PER_LEVEL_UP = 10;
export const ABILITY_POINTS_PER_LEVEL_UP = 3;

export interface CharacterStatsFromTotalExp {
	currentLevel: number;
	currentExp: number;
	requiredExp: number;
}

export function deriveCharacterStatsFromTotalExp(
	totalExp: number,
): CharacterStatsFromTotalExp {
	let nextExp = CHARACTER_STARTING_EXP + Math.max(0, Math.floor(totalExp));
	let nextLevel = CHARACTER_STARTING_LEVEL;
	let nextRequired = CHARACTER_STARTING_REQUIRED_EXP;

	while (nextExp >= nextRequired) {
		nextExp -= nextRequired;
		nextLevel += 1;
		nextRequired = Math.floor(nextRequired * REQUIRED_EXP_MULTIPLIER);
	}

	return {
		currentLevel: nextLevel,
		currentExp: nextExp,
		requiredExp: nextRequired,
	};
}

export interface ApplyExpGainResult {
	next: CharacterStatsFromTotalExp;
	levelsGained: number;
}

/**
 * Applies a non-negative EXP gain to a bar/level snapshot (same progression as learn HUD).
 */
export function applyExpGain(
	state: CharacterStatsFromTotalExp,
	amount: number,
): ApplyExpGainResult {
	let nextExp = state.currentExp + Math.max(0, Math.floor(amount));
	let nextLevel = state.currentLevel;
	let nextRequired = state.requiredExp;
	let levelsGained = 0;

	while (nextExp >= nextRequired) {
		nextExp -= nextRequired;
		nextLevel += 1;
		levelsGained += 1;
		nextRequired = Math.floor(nextRequired * REQUIRED_EXP_MULTIPLIER);
	}

	return {
		next: {
			currentLevel: nextLevel,
			currentExp: nextExp,
			requiredExp: nextRequired,
		},
		levelsGained,
	};
}
